import { logScenarioStart, logScenarioStop } from './scenarioTelemetry';
import type { ScenarioId, ScenarioName, ScenarioSource, ScenarioStatus } from './types';

const defaultTimeoutInMS = 30_000;

export type PageLoadScenarioName = Extract<ScenarioName, `open_${string}`>;

export interface StartPageLoadScenarioOptions {
  /** The navigation scenario this page load represents (e.g. `open_community_feed`). */
  readonly name: PageLoadScenarioName;
  /** The entry point that triggered the navigation (e.g. `engage_left_nav`). */
  readonly source?: ScenarioSource;
  /** How long to wait for the page to paint before the façade auto-reports `timeout`. */
  readonly timeoutInMS?: number;
  /** Fired once with the terminal status when the handle settles the scenario (not for `timeout`). */
  readonly reportScenarioSettled?: (status: ScenarioStatus) => void;
}

export interface PageLoadScenarioHandle {
  /** The started scenario's id, for correlating call-ledger rows or markers to this page load. */
  readonly scenarioId: ScenarioId;
  /** Stops the scenario as `ok`; call when the page's hero content has painted. A no-op once settled. */
  readonly reportPageShown: () => void;
  /**
   * Stops the scenario as `failure`; the optional error's name/message become `errorCode`/
   * `errorMessage`. A no-op once settled.
   */
  readonly reportPageLoadFailure: (error?: Error) => void;
  /**
   * Stops the scenario as `ignore`; use for an expected, recoverable page-load failure that must
   * retain expected-error semantics rather than count against reliability. The optional error's
   * name/message become `errorCode`/`errorMessage`. A no-op once settled.
   */
  readonly reportRecoverablePageLoadFailure: (error?: Error) => void;
  /** Stops the scenario as `cancel`; call when the user navigates away before the page paints. A no-op once settled. */
  readonly abandon: () => void;
}

export const startPageLoadScenario = ({
  name,
  source,
  timeoutInMS = defaultTimeoutInMS,
  reportScenarioSettled,
}: StartPageLoadScenarioOptions): PageLoadScenarioHandle => {
  const scenarioId = logScenarioStart({ name, source, timeoutInMS });

  let settled = false;

  const markScenarioTimedOut = () => {
    settled = true;
  };
  const guardTimer = window.setTimeout(markScenarioTimedOut, timeoutInMS);

  const stop = (status: ScenarioStatus, error?: Error) => {
    if (settled) {
      return;
    }
    settled = true;
    window.clearTimeout(guardTimer);
    logScenarioStop({ scenarioId, status, ...(error ? { error } : {}) });
    reportScenarioSettled?.(status);
  };

  return {
    scenarioId,
    reportPageShown: () => {
      stop('ok');
    },
    reportPageLoadFailure: (error?: Error) => {
      stop('failure', error);
    },
    reportRecoverablePageLoadFailure: (error?: Error) => {
      stop('ignore', error);
    },
    abandon: () => {
      stop('cancel');
    },
  };
};
